import { useState } from 'react'
import { AxiosError } from 'axios'
import { toast } from 'react-toastify'

import {
  NewPasswordFormData,
  User,
  UserFormData,
  UserProfileFormData,
} from '@/models/user'
import { ApiError } from '@/models/apiError'
import api from '@/shared/api'
import { UTILS, API_ERRORS, ENDPOINTS } from '@/shared/constants'
import { logged, unlogged, updateLoggedUser } from '@/store/features/authSlice'
import { store } from '@/store'
import useRouting from './useRouting'

export default function useUsersApi() {
  const { getRouteWithId } = useRouting()
  const [users, setUsers] = useState<User[]>([])
  const [user, setUser] = useState<User>()

  async function getUserLogged() {
    try {
      const res = await api.get(ENDPOINTS.USERS.GET.PROFILE)
      store.dispatch(logged(res.data))
    } catch (error) {
      const status = (error as AxiosError).response?.status

      if (status === 401) {
        toast.error('Sessão expirada. Faça login novamente')
        store.dispatch(unlogged())
        return
      }

      toast.error('Erro ao validar sessão')
    }
  }

  async function getUsers() {
    try {
      const res = await api.get(ENDPOINTS.USERS.GET.ALL)
      setUsers(res.data)
    } catch {
      toast.error('Erro ao listar usuários')
    }
  }

  async function getUser(id: string) {
    try {
      const res = await api.get(getRouteWithId(ENDPOINTS.USERS.GET.BY_ID, id))
      setUser(res.data)
    } catch {
      toast.error('Erro ao obter detalhes do usuário')
    }
  }

  async function createUser(data: UserFormData) {
    try {
      await api.post(ENDPOINTS.USERS.POST.CREATE, parseToSave(data))
      toast.success('Usuário criado')
      return true
    } catch (error) {
      const apiError = error as ApiError
      handleRequestError(apiError?.response?.data?.errorCode)
      return false
    }
  }

  async function updateUser(id: string, data: UserFormData) {
    try {
      await api.patch(
        getRouteWithId(ENDPOINTS.USERS.PATCH.BY_ID, id),
        parseToSave(data)
      )
      toast.success('Usuário atualizado')
      return true
    } catch (error) {
      const apiError = error as ApiError
      handleRequestError(apiError?.response?.data?.errorCode)
      return false
    }
  }

  async function updateProfile(data: UserProfileFormData) {
    try {
      const res = await api.patch<User>(
        ENDPOINTS.USERS.PATCH.PROFILE,
        parseProfileToSave(data)
      )

      store.dispatch(updateLoggedUser(res.data))
      toast.success('Perfil atualizado')
      return true
    } catch (error) {
      const apiError = error as ApiError
      handleRequestError(
        apiError.response?.data?.errorCode,
        'Erro ao atualizar perfil'
      )
      return false
    }
  }

  async function createPassword(data: NewPasswordFormData) {
    try {
      await api.post(ENDPOINTS.USERS.POST.CREATE_PASSWORD, data)
      toast.success('Senha criada')
      return true
    } catch (error) {
      const apiError = error as ApiError
      handleRequestError(
        apiError.response?.data?.errorCode,
        'Erro ao criar senha'
      )
      return false
    }
  }

  async function requestResetPassword(email: string) {
    try {
      await api.post(ENDPOINTS.USERS.POST.REQUEST_RESET_PASSWORD, { email })
      toast.success(
        'Caso haja uma conta associada, o link foi enviado para seu e-mail'
      )
      return true
    } catch {
      toast.error('Erro ao solicitar mudança de senha')
      return false
    }
  }

  async function deleteUser(id: string) {
    try {
      await api.delete(getRouteWithId(ENDPOINTS.USERS.DELETE.BY_ID, id))
      toast.success('Usuário removido')
      return true
    } catch (error) {
      const apiError = error as ApiError
      handleRequestError(
        apiError?.response?.data?.errorCode,
        'Erro ao remover usuário'
      )
      return false
    }
  }

  async function uploadProfileAvatar(file: File) {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const res = await api.patch<User>(
        ENDPOINTS.USERS.PATCH.PROFILE_AVATAR,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      store.dispatch(updateLoggedUser(res.data))
      toast.success('Foto de perfil atualizada')
      return res.data
    } catch (error) {
      const apiError = error as ApiError
      handleRequestError(
        apiError.response?.data?.errorCode,
        'Não foi possível atualizar a foto de perfil.'
      )
      return false
    }
  }

  async function removeProfileAvatar() {
    try {
      const res = await api.delete<User>(ENDPOINTS.USERS.DELETE.PROFILE_AVATAR)
      store.dispatch(updateLoggedUser(res.data))
      toast.success('Foto de perfil removida')
      return res.data
    } catch (error) {
      const apiError = error as ApiError
      handleRequestError(
        apiError.response?.data?.errorCode,
        'Não foi possível remover a foto de perfil.'
      )
      return false
    }
  }

  function parseToSave(data: UserFormData) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { countryCode, ...payload } = data

    return {
      ...payload,
      cellPhone:
        data.countryCode && data.cellPhone
          ? `${data.countryCode}${data.cellPhone.replace(/\D/g, UTILS.BLANK)}`
          : null,
      roleId: +data.roleId,
      companyId: data.companyId ? +data.companyId : undefined,
      departmentIds: data.departmentIds?.map((departmentId) => +departmentId),
    }
  }

  function parseProfileToSave(data: UserProfileFormData) {
    return {
      name: data.name,
      email: data.email,
      cellPhone:
        data.countryCode && data.cellPhone
          ? `${data.countryCode}${data.cellPhone.replace(/\D/g, UTILS.BLANK)}`
          : null,
      acessoriasApiKey: data.acessoriasApiKey,
      conversationNotificationPreference: data.conversationNotificationPreference,
    }
  }

  function handleRequestError(
    errorCode?: number,
    fallbackMessage = 'Erro ao salvar usuário'
  ) {
    if (errorCode && API_ERRORS[errorCode]) {
      toast.error(API_ERRORS[errorCode])
    } else {
      toast.error(fallbackMessage)
    }
  }

  return {
    getUserLogged,
    getUsers,
    getUser,
    createUser,
    updateUser,
    updateProfile,
    deleteUser,
    uploadProfileAvatar,
    removeProfileAvatar,
    createPassword,
    requestResetPassword,
    users,
    user,
  }
}
